// src/components/dashboard/livegrid/GridAiPanel.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Live Grid AI
//   useFlowSenseAsk()  ask Ollama (FlowSense AI) with a context; falls back to rules
//   <GridAiStrip />    compact overlay on the map: grid score, headline, top priority
//   <GridAiPanel />    drawer: Priorities · Ask AI · Live · Ecosystem
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, IconButton, Icon, Input, Tooltip, Spinner,
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
    Tabs, TabList, Tab, TabPanels, TabPanel, Tag, Wrap, WrapItem, Progress, useColorModeValue,
} from '@chakra-ui/react';
import { FaBrain, FaCrosshairs, FaRobot } from 'react-icons/fa';
import { MdSend, MdArrowForward, MdAutoAwesome } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { askFlowSenseAi } from '../../../services/flowsenseAiService';
import { answerGridLocally, buildGridAiContext, GRID_QUESTIONS } from './liveGridIntel';
import { FLOWSENSE_MODULES } from '../ai/flowsenseModules';
import { describeLiveEvent } from '../../../hooks/useFlowSenseLive';

const LEVEL_COLOR = { critical: 'red', high: 'orange', medium: 'yellow', low: 'green' };

/** Ask FlowSense AI (Ollama). On any failure answer from `fallback()` and label it. */
export function useFlowSenseAsk() {
    const [isAsking, setIsAsking] = useState(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const ask = useCallback(async (question, context, module, fallback) => {
        setIsAsking(true);
        try {
            const r = await askFlowSenseAi(question, context, module);
            if (!r?.answer) throw new Error('Empty answer');
            return { answer: r.answer, source: 'ollama', model: r.model };
        } catch (err) {
            const local = typeof fallback === 'function' ? fallback() : { answer: 'FlowSense AI is unavailable right now.' };
            return { ...local, source: 'rules', note: err?.message || 'AI offline' };
        } finally {
            if (mounted.current) setIsAsking(false);
        }
    }, []);
    return { ask, isAsking };
}

export const SourceBadge = ({ source, model, note }) => (
    <Tooltip hasArrow label={source === 'ollama' ? 'Answered by the Ollama model using the live grid snapshot.' : `Answered by FlowSense's built-in rules${note ? ` (${note})` : ''}.`}>
        <Badge colorScheme={source === 'ollama' ? 'purple' : 'gray'} fontSize="2xs">{source === 'ollama' ? `Ollama${model ? ` · ${model}` : ''}` : 'FlowSense rules'}</Badge>
    </Tooltip>
);
SourceBadge.propTypes = { source: PropTypes.string, model: PropTypes.string, note: PropTypes.string };

const EngineChips = ({ aiStatus, wsConnected }) => {
    const ollama = aiStatus?.ollama;
    const ml = aiStatus?.ml;
    const chip = (label, state, tip) => (
        <WrapItem key={label}>
            <Tooltip hasArrow label={tip}>
                <Tag size="sm" variant={state === 'online' ? 'subtle' : 'outline'} colorScheme={state === 'online' ? 'green' : state === 'offline' ? 'red' : 'gray'} aria-label={`${label} ${state}`}>
                    {label}: {state}
                </Tag>
            </Tooltip>
        </WrapItem>
    );
    return (
        <Wrap spacing={1}>
            {chip('Ollama', ollama ? (ollama.online ? 'online' : 'offline') : 'unknown', ollama?.model ? `Model ${ollama.model}${ollama.online && ollama.model_installed === false ? ' (not pulled)' : ''}` : 'Language model for briefs and answers')}
            {chip('ML', ml ? (ml.reachable ? 'online' : 'offline') : 'unknown', ml?.circuit?.state ? `Circuit ${ml.circuit.state}` : 'Failure-risk / RUL models')}
            {chip('Live', wsConnected === undefined ? 'unknown' : wsConnected ? 'online' : 'offline', 'WebSocket stream (status, telemetry, anomalies, predictions)')}
        </Wrap>
    );
};
EngineChips.propTypes = { aiStatus: PropTypes.object, wsConnected: PropTypes.bool };

/** Compact overlay on the map. */
export function GridAiStrip({ brief, onOpen, onFocusAsset }) {
    const bg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(26,32,44,0.92)');
    const muted = useColorModeValue('gray.600', 'gray.300');
    if (!brief) return null;
    const top = brief.priorities.find(p => !p.covered && (p.level === 'critical' || p.level === 'high'));
    return (
        <Box bg={bg} backdropFilter="blur(12px)" borderRadius="xl" boxShadow="lg" px={3} py={2} maxW="420px" borderWidth="1px" borderColor="purple.300" role="region" aria-label="Grid AI summary">
            <HStack spacing={2} mb={top ? 1 : 0}>
                <Icon as={FaBrain} color="purple.400" />
                <Tooltip hasArrow label={brief.deductions.length ? brief.deductions.map(d => `−${d.points} ${d.why}`).join(' · ') : 'No deductions'}>
                    <Badge colorScheme={brief.grade.color} cursor="help" aria-label={`Grid score ${brief.score}`}>{brief.score} · {brief.grade.label}</Badge>
                </Tooltip>
                <Text fontSize="xs" color={muted} noOfLines={2} flex={1}>{brief.headline}</Text>
                <Button size="xs" colorScheme="purple" variant="ghost" onClick={onOpen} rightIcon={<Icon as={MdArrowForward} />}>Grid AI</Button>
            </HStack>
            {top && (
                <HStack spacing={2}>
                    <Badge colorScheme={LEVEL_COLOR[top.level]} fontSize="2xs">{top.level}</Badge>
                    <Text fontSize="xs" fontWeight="700" noOfLines={1} flex={1}>{top.asset.name}</Text>
                    <Button size="xs" leftIcon={<Icon as={FaCrosshairs} />} onClick={() => onFocusAsset(top.asset)} aria-label={`Focus ${top.asset.name}`}>Focus</Button>
                </HStack>
            )}
        </Box>
    );
}
GridAiStrip.propTypes = { brief: PropTypes.object, onOpen: PropTypes.func.isRequired, onFocusAsset: PropTypes.func.isRequired };

export default function GridAiPanel({
    isOpen, onClose, brief, assets, selectedAsset, onFocusAsset, aiStatus, wsConnected,
    feed, systemBrief, onOpenGuide,
}) {
    const navigate = useNavigate();
    const { ask, isAsking } = useFlowSenseAsk();
    const [tab, setTab] = useState(0);
    const [question, setQuestion] = useState('');
    const [history, setHistory] = useState([]);
    const cardBg = useColorModeValue('gray.50', 'gray.700');
    const muted = useColorModeValue('gray.600', 'gray.400');

    const submit = useCallback(async (q) => {
        const text = String(q || '').trim();
        if (!text || isAsking) return;
        setQuestion('');
        const ctx = buildGridAiContext(brief, selectedAsset);
        if (systemBrief) ctx.system = { score: systemBrief.score, grade: systemBrief.grade?.label, headline: systemBrief.headline };
        const r = await ask(text, ctx, 'live_grid', () => answerGridLocally(text, brief, assets));
        setHistory(h => [{ q: text, ...r, at: Date.now() }, ...h].slice(0, 8));
    }, [ask, brief, assets, selectedAsset, systemBrief, isAsking]);

    const gridEvents = (feed?.events || []).filter(e => ['alerts', 'predictions', 'assets', 'readings', 'workOrders'].includes(e.group)).slice(0, 25);

    return (
        <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px">
                    <HStack spacing={2}>
                        <Icon as={MdAutoAwesome} color="purple.500" />
                        <Text>Grid AI</Text>
                        {brief && <Badge colorScheme={brief.grade.color}>{brief.score} · {brief.grade.label}</Badge>}
                    </HStack>
                    <Text fontSize="sm" fontWeight="normal" color={muted} mt={1}>{brief?.headline}</Text>
                    <Box mt={2}><EngineChips aiStatus={aiStatus} wsConnected={wsConnected} /></Box>
                </DrawerHeader>
                <DrawerBody>
                    <Tabs size="sm" colorScheme="purple" index={tab} onChange={setTab} isLazy>
                        <TabList flexWrap="wrap">
                            <Tab>Priorities</Tab>
                            <Tab>Ask AI</Tab>
                            <Tab>Live{gridEvents.length ? ` (${gridEvents.length})` : ''}</Tab>
                            <Tab>Ecosystem</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel px={0}>
                                {brief && (
                                    <VStack align="stretch" spacing={2} mb={3}>
                                        <HStack spacing={2} flexWrap="wrap">
                                            <Tag size="sm" colorScheme="green">{brief.health.optimal} optimal</Tag>
                                            <Tag size="sm" colorScheme="orange">{brief.health.warning} deteriorating</Tag>
                                            <Tag size="sm" colorScheme="red">{brief.health.critical} critical</Tag>
                                            <Tag size="sm">{brief.totals.offline} offline</Tag>
                                            <Tag size="sm" colorScheme="purple">{brief.totals.mlHighRisk} ML risk ≥70%</Tag>
                                            <Tag size="sm" colorScheme="pink">{brief.totals.failingSoon} fail ≤30d</Tag>
                                            {brief.totals.anomalies > 0 && <Tag size="sm" colorScheme="red" variant="solid">{brief.totals.anomalies} live anomalies</Tag>}
                                        </HStack>
                                    </VStack>
                                )}
                                {!brief?.priorities.length ? (
                                    <Text fontSize="sm" color={muted}>Nothing needs attention on the grid.</Text>
                                ) : (
                                    <VStack align="stretch" spacing={2} as="ol" style={{ listStyle: 'none' }} aria-label="Grid priorities">
                                        {brief.priorities.map((p) => (
                                            <Box as="li" key={p.asset.id} bg={cardBg} borderRadius="lg" p={3} borderLeftWidth="4px" borderLeftColor={`${LEVEL_COLOR[p.level]}.400`}>
                                                <Flex justify="space-between" align="flex-start" gap={2}>
                                                    <Box minW={0}>
                                                        <HStack spacing={2}>
                                                            <Text fontWeight="700" fontSize="sm" noOfLines={1}>{p.asset.name}</Text>
                                                            <Badge colorScheme={LEVEL_COLOR[p.level]} fontSize="2xs">{p.level}</Badge>
                                                            {p.covered && <Badge colorScheme="blue" fontSize="2xs">WO active</Badge>}
                                                        </HStack>
                                                        <Text fontSize="xs" color={muted}>{p.asset.type || 'Asset'}{p.asset.zone_name ? ` · ${p.asset.zone_name}` : ''}</Text>
                                                        <Text fontSize="xs" mt={1}>{p.reasons.slice(0, 3).join(' · ')}</Text>
                                                        <Text fontSize="xs" mt={1} color="purple.500">{p.recommendation}</Text>
                                                    </Box>
                                                    <IconButton size="xs" icon={<Icon as={FaCrosshairs} />} aria-label={`Focus ${p.asset.name}`} onClick={() => { onFocusAsset(p.asset); onClose(); }} />
                                                </Flex>
                                                <Progress value={p.score} size="xs" mt={2} colorScheme={LEVEL_COLOR[p.level]} borderRadius="full" aria-label={`Priority score ${p.score}`} />
                                            </Box>
                                        ))}
                                    </VStack>
                                )}
                                {brief?.worstZones?.length > 0 && (
                                    <Box mt={4}>
                                        <Text fontSize="xs" fontWeight="700" mb={1}>Zones with the lowest health</Text>
                                        {brief.worstZones.map(z => (
                                            <Text key={z.id} fontSize="xs" color={muted}>• {z.name}: {z.avgHealth}% · {z.activeWorkOrders} active WOs · {z.totalAssets} assets</Text>
                                        ))}
                                    </Box>
                                )}
                            </TabPanel>

                            <TabPanel px={0}>
                                <Wrap spacing={1} mb={2}>
                                    {GRID_QUESTIONS.map(s => (
                                        <WrapItem key={s}><Button size="xs" variant="outline" colorScheme="purple" onClick={() => submit(s)} isDisabled={isAsking}>{s}</Button></WrapItem>
                                    ))}
                                    {selectedAsset && (
                                        <WrapItem><Button size="xs" colorScheme="purple" onClick={() => submit(`Assess ${selectedAsset.name} and tell me what to do next.`)} isDisabled={isAsking}>About {selectedAsset.name}</Button></WrapItem>
                                    )}
                                </Wrap>
                                <HStack as="form" onSubmit={(e) => { e.preventDefault(); submit(question); }} mb={3}>
                                    <Input size="sm" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask about the grid, an asset or a zone…" aria-label="Ask Grid AI" maxLength={1000} />
                                    <IconButton type="submit" size="sm" colorScheme="purple" icon={isAsking ? <Spinner size="xs" /> : <Icon as={MdSend} />} isDisabled={isAsking || !question.trim()} aria-label="Send grid question" />
                                </HStack>
                                <VStack align="stretch" spacing={3} aria-live="polite">
                                    {history.map(h => (
                                        <Box key={h.at} bg={cardBg} borderRadius="lg" p={3}>
                                            <HStack justify="space-between" mb={1}>
                                                <Text fontSize="xs" fontWeight="700" noOfLines={1}>{h.q}</Text>
                                                <SourceBadge source={h.source} model={h.model} note={h.note} />
                                            </HStack>
                                            <Text fontSize="sm" whiteSpace="pre-wrap">{h.answer}</Text>
                                        </Box>
                                    ))}
                                    {!history.length && <Text fontSize="xs" color={muted}>Questions go to the Ollama model with a snapshot of the grid (scores, priorities, zones and the selected asset). If Ollama is offline, FlowSense answers from its built-in rules.</Text>}
                                </VStack>
                            </TabPanel>

                            <TabPanel px={0}>
                                {!gridEvents.length ? (
                                    <Text fontSize="sm" color={muted}>No grid events yet. Status changes, readings, anomalies, ML predictions and work order updates appear here the moment the WebSocket delivers them.</Text>
                                ) : (
                                    <VStack align="stretch" spacing={1.5} as="ul" style={{ listStyle: 'none' }} aria-label="Grid live activity">
                                        {gridEvents.map(e => (
                                            <HStack as="li" key={e.id} spacing={2} fontSize="xs">
                                                <Badge fontSize="2xs" colorScheme={e.group === 'alerts' ? 'red' : e.group === 'predictions' ? 'purple' : 'blue'}>{e.group}</Badge>
                                                <Text flex={1} noOfLines={1}>{e.label || describeLiveEvent(e.name, e.data)}</Text>
                                                <Text color={muted}>{new Date(e.at).toLocaleTimeString()}</Text>
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </TabPanel>

                            <TabPanel px={0}>
                                {systemBrief && (
                                    <Box bg={cardBg} borderRadius="lg" p={3} mb={3}>
                                        <HStack mb={1}><Icon as={FaRobot} color="purple.400" /><Text fontWeight="700" fontSize="sm">FlowSense system</Text><Badge colorScheme={systemBrief.grade?.color}>{systemBrief.score} · {systemBrief.grade?.label}</Badge></HStack>
                                        <Text fontSize="xs" color={muted}>{systemBrief.headline}</Text>
                                        {(systemBrief.priorities || []).filter(p => p.level !== 'info').slice(0, 3).map(p => (
                                            <HStack key={p.id} mt={1.5} justify="space-between">
                                                <Text fontSize="xs" noOfLines={1}>• {p.title}</Text>
                                                {p.route && <Button size="xs" variant="link" colorScheme="purple" onClick={() => { onClose(); navigate(p.route); }}>Go</Button>}
                                            </HStack>
                                        ))}
                                    </Box>
                                )}
                                <Text fontSize="xs" fontWeight="700" mb={2}>Connected modules</Text>
                                <VStack align="stretch" spacing={2}>
                                    {['work_orders', 'public_utilities', 'live_readings', 'digital_twin', 'road_works', 'flowsense_hub'].map(k => {
                                        const m = FLOWSENSE_MODULES[k];
                                        if (!m) return null;
                                        return (
                                            <Flex key={k} bg={cardBg} borderRadius="lg" p={2.5} align="center" gap={2}>
                                                <Box flex={1} minW={0}>
                                                    <Text fontSize="sm" fontWeight="700">{m.name}</Text>
                                                    <Text fontSize="xs" color={muted} noOfLines={2}>{m.summary}</Text>
                                                </Box>
                                                {m.route && <Button size="xs" colorScheme={m.color} variant="outline" rightIcon={<Icon as={MdArrowForward} />} onClick={() => { onClose(); navigate(m.route); }}>Open</Button>}
                                            </Flex>
                                        );
                                    })}
                                </VStack>
                                {onOpenGuide && <Button mt={3} size="sm" w="100%" variant="ghost" colorScheme="purple" onClick={() => { onClose(); onOpenGuide(); }}>Open the FlowSense guide</Button>}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}

GridAiPanel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    brief: PropTypes.object,
    assets: PropTypes.array,
    selectedAsset: PropTypes.object,
    onFocusAsset: PropTypes.func.isRequired,
    aiStatus: PropTypes.object,
    wsConnected: PropTypes.bool,
    feed: PropTypes.object,
    systemBrief: PropTypes.object,
    onOpenGuide: PropTypes.func,
};
